import AddIcon from '@mui/icons-material/Add';
import {
  Box,
  Button,
  MenuItem,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import { useState } from 'react';
import { useMijnContactmomenten } from '../../api/contactmomenten.ts';
import { zonderLeeg } from '../../api/mutaties.ts';
import type { Contactkanaal, GesprekFilter } from '../../api/typen.ts';
import { ContactmomentDialoog } from '../../componenten/ContactmomentDialoog.tsx';
import { ContactmomentenLijst } from '../../componenten/ContactmomentenLijst.tsx';
import { kanalen } from '../../componenten/gesprek.tsx';
import { Pagina } from '../../componenten/Pagina.tsx';
import { Zoekveld } from '../../componenten/Zoekveld.tsx';
import { contactkanaalLabels } from '../../helpers/labels.ts';
import { useZoekparameters } from '../../hooks/useZoekparameters.ts';

const kanaalvolgorde = Object.keys(kanalen) as Contactkanaal[];
const datumPatroon = /^\d{4}-\d{2}-\d{2}$/;
const filterparameters = ['zoek', 'kanaal', 'richting', 'met', 'van', 'tot'] as const;

/**
 * Zet de filters uit het adres om naar de queryparameters van de API. Onbekende waarden vallen weg.
 * @param krijg Leest een parameter uit het adres.
 * @returns De filters, zonder paginering.
 */
export function gesprekFilterUitAdres(krijg: (naam: string) => string): GesprekFilter {
  const kanaal = krijg('kanaal')
    .split(',')
    .filter((waarde): waarde is Contactkanaal => (kanaalvolgorde as string[]).includes(waarde));
  const richting = krijg('richting');
  const met = krijg('met');
  return zonderLeeg({
    zoek: krijg('zoek').trim() || undefined,
    kanaal: kanaal.length > 0 ? kanaal : undefined,
    richting: richting === 'inkomend' || richting === 'uitgaand' ? richting : undefined,
    met: met === 'kandidaat' || met === 'contactpersoon' ? met : undefined,
    van: datumPatroon.test(krijg('van')) ? krijg('van') : undefined,
    tot: datumPatroon.test(krijg('tot')) ? krijg('tot') : undefined,
  });
}

/**
 * De gesprekken die de aangemelde gebruiker zelf heeft vastgelegd, te filteren op tekst, kanaal, richting, met wie
 * en periode. De filters staan in het adres, zodat de knop terug werkt.
 * @returns De pagina.
 */
export function GesprekkenPagina() {
  const { offset, limiet, krijg, zet, zetOffset } = useZoekparameters(25);
  const filter = gesprekFilterUitAdres(krijg);
  const gesprekken = useMijnContactmomenten({ ...filter, limiet, ...(offset > 0 ? { offset } : {}) });
  const [nieuw, setNieuw] = useState(false);
  const gefilterd = Object.keys(filter).length > 0;
  const gekozenKanalen = filter.kanaal ?? [];

  return (
    <Pagina
      titel="Mijn gesprekken"
      ondertitel="Wat jij hebt vastgelegd, nieuwste eerst. Gesprekken van collega's staan bij de kandidaat, contactpersoon, bedrijf of vacature."
      acties={
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setNieuw(true)}>
          Gesprek vastleggen
        </Button>
      }
    >
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" useFlexGap spacing={1.5} sx={{ flexWrap: 'wrap', alignItems: 'center' }}>
          <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 260px' } }}>
            <Zoekveld
              waarde={krijg('zoek')}
              onChange={(waarde) => zet({ zoek: waarde })}
              placeholder="Zoek in notitie of op naam"
            />
          </Box>
          <ToggleButtonGroup
            size="small"
            value={gekozenKanalen}
            onChange={(_gebeurtenis, waarden: Contactkanaal[]) => zet({ kanaal: waarden.join(',') })}
            aria-label="Kanaal"
          >
            {kanaalvolgorde.map((kanaal) => (
              <ToggleButton key={kanaal} value={kanaal} aria-label={contactkanaalLabels[kanaal]} sx={{ px: 1.25 }}>
                <Tooltip title={contactkanaalLabels[kanaal]}>
                  <Box component="span" sx={{ display: 'flex' }}>
                    {kanalen[kanaal].icoon(18)}
                  </Box>
                </Tooltip>
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <TextField
            select
            size="small"
            label="Richting"
            value={filter.richting ?? ''}
            onChange={(gebeurtenis) => zet({ richting: gebeurtenis.target.value })}
            slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}
            sx={{ minWidth: 130 }}
          >
            <MenuItem value="">Beide</MenuItem>
            <MenuItem value="uitgaand">Uitgaand</MenuItem>
            <MenuItem value="inkomend">Inkomend</MenuItem>
          </TextField>
          <TextField
            select
            size="small"
            label="Met"
            value={filter.met ?? ''}
            onChange={(gebeurtenis) => zet({ met: gebeurtenis.target.value })}
            slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="">Iedereen</MenuItem>
            <MenuItem value="kandidaat">Kandidaten</MenuItem>
            <MenuItem value="contactpersoon">Contactpersonen</MenuItem>
          </TextField>
          <TextField
            size="small"
            type="date"
            label="Van"
            value={filter.van ?? ''}
            onChange={(gebeurtenis) => zet({ van: gebeurtenis.target.value })}
            slotProps={{ inputLabel: { shrink: true } }}
            sx={{ width: 160 }}
          />
          <TextField
            size="small"
            type="date"
            label="Tot en met"
            value={filter.tot ?? ''}
            onChange={(gebeurtenis) => zet({ tot: gebeurtenis.target.value })}
            slotProps={{ inputLabel: { shrink: true } }}
            sx={{ width: 160 }}
          />
          {gefilterd && (
            <Button
              size="small"
              onClick={() => zet(Object.fromEntries(filterparameters.map((naam) => [naam, undefined])))}
            >
              Filters wissen
            </Button>
          )}
        </Stack>
      </Paper>
      <Paper sx={{ p: 2 }}>
        <ContactmomentenLijst
          query={gesprekken}
          toon={{ gebruiker: false }}
          leeg={gefilterd ? 'Geen gesprekken gevonden met deze filters.' : 'Je hebt nog geen gesprekken vastgelegd.'}
          paginering={{ offset, limiet, onOffset: zetOffset }}
        />
      </Paper>
      <ContactmomentDialoog open={nieuw} onSluit={() => setNieuw(false)} />
    </Pagina>
  );
}
